import React, { useEffect, useRef, useState } from "react";

// Splits a stat like "1000+", "35%", "250+" into prefix / number / suffix so
// only the numeric part animates and the "+", "%" etc. are preserved.
const parseValue = (value) => {
  const match = String(value).match(/^(\D*)(\d[\d,]*)(.*)$/);
  if (!match) return { prefix: "", number: null, suffix: String(value) };
  return {
    prefix: match[1] || "",
    number: parseInt(match[2].replace(/,/g, ""), 10),
    suffix: match[3] || "",
  };
};

// Counts up from 0 → target the first time it scrolls into view.
// Respects prefers-reduced-motion (renders the final value immediately).
const CountUp = ({ value, duration = 1600 }) => {
  const { prefix, number, suffix } = parseValue(value);
  const [display, setDisplay] = useState(
    number === null ? String(value) : `${prefix}0${suffix}`
  );
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (number === null) return;
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      setDisplay(`${prefix}${number}${suffix}`);
      return;
    }

    let rafId;
    const animate = () => {
      let start = null;
      const tick = (now) => {
        if (start === null) start = now;
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setDisplay(`${prefix}${Math.round(eased * number)}${suffix}`);
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            animate();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [number, prefix, suffix, duration, value]);

  return <span ref={ref}>{display}</span>;
};

export default CountUp;
